import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { CompanyInfo } from '../types';
import { API_BASE_URL } from '../config/api';

/**
 * Holds the company's locked business module (retail | grocery | pharmacy |
 * hardware | restaurant). Navigation and routes adapt to it; the backend
 * enforces it independently via the JWT module claim, so this is purely UX.
 */
interface ModuleContextType {
  company: CompanyInfo | null;
  moduleCode: string;
  isModule: (code: string) => boolean;
  setCompany: (company: CompanyInfo | null) => void;
}

// Legacy sessions predate module selection and belong to installs that had
// every feature enabled — mirror the backend's restaurant fallback.
const LEGACY_MODULE_CODE = 'restaurant';

const ModuleContext = createContext<ModuleContextType | null>(null);

function loadFromStorage(): CompanyInfo | null {
  try {
    const raw = localStorage.getItem('company');
    return raw ? (JSON.parse(raw) as CompanyInfo) : null;
  } catch {
    return null;
  }
}

export const ModuleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [company, setCompanyState] = useState<CompanyInfo | null>(loadFromStorage());

  const setCompany = useCallback((next: CompanyInfo | null) => {
    if (next) {
      localStorage.setItem('company', JSON.stringify(next));
    } else {
      localStorage.removeItem('company');
    }
    setCompanyState(next);
  }, []);

  // Refresh from /auth/me so a session started before module support (or on
  // another device) picks up its company without re-login.
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token || company) return;
    fetch(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        const fresh = json?.data?.company as CompanyInfo | undefined;
        if (fresh) setCompany(fresh);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const moduleCode = company?.moduleCode ?? LEGACY_MODULE_CODE;

  const isModule = useCallback(
    (code: string) => moduleCode === code,
    [moduleCode]
  );

  return (
    <ModuleContext.Provider value={{ company, moduleCode, isModule, setCompany }}>
      {children}
    </ModuleContext.Provider>
  );
};

export function useModule(): ModuleContextType {
  const ctx = useContext(ModuleContext);
  if (!ctx) throw new Error('useModule must be used inside ModuleProvider');
  return ctx;
}
